import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { api, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { Users, Trophy, Loader2, Wallet, Coins, Megaphone, Lock, Bot } from "lucide-react";
import { toast } from "sonner";

const ANN_STYLES = {
  info: "bg-[#3B82F6]/15 border-[#3B82F6]/40 text-[#93C5FD]",
  warning: "bg-[#F59E0B]/15 border-[#F59E0B]/40 text-[#FCD34D]",
  success: "bg-[#10B981]/15 border-[#10B981]/40 text-[#6EE7B7]",
};

export default function Lobby() {
  const { user } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [currency, setCurrency] = useState("real");
  const [showAnn, setShowAnn] = useState(false);
  const [privModal, setPrivModal] = useState(null); // 'create' | 'join'
  const [pName, setPName] = useState("");
  const [pPass, setPPass] = useState("");
  const [pStake, setPStake] = useState(5);
  const [pCur, setPCur] = useState("real");
  const [jCode, setJCode] = useState("");
  const [jPass, setJPass] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/lobby");
      setData(data);
    } catch (e) {
      if (e?.response?.status === 401) navigate("/");
    }
  }, [navigate]);

  useEffect(() => { load(); const t = setInterval(load, 4000); return () => clearInterval(t); }, [load]);

  useEffect(() => {
    if (branding?.announcement_enabled && branding?.announcement_modal && branding?.announcement_text) {
      if (sessionStorage.getItem("dr_ann_seen") !== branding.announcement_text) setShowAnn(true);
    }
  }, [branding]);

  const dismissAnn = () => { setShowAnn(false); sessionStorage.setItem("dr_ann_seen", branding?.announcement_text || ""); };

  const isVirtual = currency === "virtual";
  const bal = isVirtual ? (user?.virtual_credits || 0) : (user?.credits || 0);
  const fmt = (n) => (isVirtual ? `${Number(n).toLocaleString("pt-BR")} fichas` : money(n));
  const stakeLabel = (s) => (isVirtual ? `${Number(s).toLocaleString("pt-BR")}` : `R$ ${s}`);

  const enter = (stake, cur = currency) => {
    const b = cur === "virtual" ? (user?.virtual_credits || 0) : (user?.credits || 0);
    if (b < stake) {
      toast.error(cur === "virtual" ? "Fichas insuficientes. Resgate fichas grátis na carteira." : "Saldo insuficiente. Faça um depósito na carteira.");
      navigate("/carteira");
      return;
    }
    navigate(`/game?stake=${stake}&currency=${cur}`);
  };

  const tiers = (isVirtual ? data?.virtual_tiers : data?.tiers) || [];

  const createPrivate = () => {
    const b = pCur === "virtual" ? (user?.virtual_credits || 0) : (user?.credits || 0);
    if (b < pStake) { toast.error("Saldo insuficiente para essa aposta."); return; }
    navigate("/game", { state: { mode: "create_private", stake: Number(pStake), currency: pCur, name: pName, password: pPass } });
  };
  const joinPrivate = () => {
    if (!jCode.trim()) { toast.error("Informe o código da sala."); return; }
    navigate("/game", { state: { mode: "join_private", code: jCode.trim().toUpperCase(), password: jPass } });
  };

  return (
    <div className="dr-grain min-h-screen bg-[#090B0E]">
      <Navbar />
      <main className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="font-head text-3xl sm:text-4xl font-black">Lobby</h1>
            <p className="text-[#9CA3AF] mt-1">{branding?.portal_welcome}</p>
          </div>
          <div className="flex gap-3">
            <div className="dr-card px-4 py-3 flex items-center gap-2">
              <Users size={18} className="text-[#10B981]" />
              <div><div className="font-mono-num font-bold text-lg">{data?.online_count ?? 0}</div><div className="text-[10px] text-[#9CA3AF] uppercase">Online</div></div>
            </div>
            <div className="dr-card px-4 py-3 flex items-center gap-2">
              <Trophy size={18} className="text-[#D4AF37]" />
              <div><div className="font-mono-num font-bold text-lg">{data?.total_matches ?? 0}</div><div className="text-[10px] text-[#9CA3AF] uppercase">Partidas</div></div>
            </div>
          </div>
        </div>

        {branding?.announcement_enabled && branding?.announcement_text && (
          <div className={`flex items-center gap-3 rounded-xl border px-4 py-3 mb-6 ${ANN_STYLES[branding.announcement_type] || ANN_STYLES.info}`} data-testid="announcement-banner">
            <Megaphone size={18} />
            <p className="text-sm font-medium">{branding.announcement_text}</p>
          </div>
        )}

        <div className="flex flex-wrap gap-2 mb-6" data-testid="currency-tabs">
          <button onClick={() => setCurrency("real")} data-testid="currency-real"
            className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold transition-colors ${!isVirtual ? "btn-gold" : "border border-white/10 text-[#9CA3AF]"}`}>
            <Wallet size={16} /> Reais (R$ {Number(user?.credits || 0).toFixed(2)})
          </button>
          <button onClick={() => setCurrency("virtual")} data-testid="currency-virtual"
            className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold transition-colors ${isVirtual ? "bg-[#10B981] text-black" : "border border-white/10 text-[#9CA3AF]"}`}>
            <Coins size={16} /> Fichas ({Number(user?.virtual_credits || 0).toLocaleString("pt-BR")})
          </button>
          <button onClick={() => navigate("/game", { state: { mode: "bot" } })} data-testid="play-bot-btn"
            className="flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold border border-[#D4AF37]/60 text-[#F3E5AB] hover:bg-[#D4AF37]/10 transition-colors sm:ml-auto">
            <Bot size={16} /> Jogar com Robô <span className="text-[10px] font-normal text-[#9CA3AF]">grátis · +50 fichas</span>
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="stake-grid">
          {tiers.map((t) => (
            <div key={t.stake} className="dr-card p-6 flex flex-col" data-testid={`stake-card-${t.stake}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs text-[#9CA3AF] uppercase tracking-wider">{isVirtual ? "Mesa de Fichas" : "Sala de Aposta"}</div>
                  <div className={`font-mono-num font-black text-4xl mt-1 ${isVirtual ? "text-[#10B981]" : "gold-text"}`}>{stakeLabel(t.stake)}</div>
                </div>
                <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${t.waiting > 0 ? "bg-[#10B981]/15 text-[#10B981]" : "bg-white/5 text-[#9CA3AF]"}`}>
                  {t.waiting > 0 ? "🟢 Aguardando" : "Vazia"}
                </span>
              </div>
              <div className="mt-4 flex items-center gap-4 text-sm text-[#9CA3AF]">
                <span className="flex items-center gap-1"><Users size={14} /> {t.playing} em jogo</span>
                <span className="flex items-center gap-1"><Trophy size={14} className="text-[#D4AF37]" /> Prêmio {fmt(t.stake * 2 * (t.prize_pct || 80) / 100)}</span>
              </div>
              <button onClick={() => enter(t.stake)} data-testid={`enter-stake-${t.stake}`}
                className={`mt-6 w-full py-3 rounded-full uppercase font-head ${isVirtual ? "bg-[#10B981] text-black font-extrabold" : "btn-gold"}`}>Entrar</button>
            </div>
          ))}
        </div>

        <div className="mt-8 dr-card p-6" data-testid="private-rooms">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="font-head text-xl font-bold flex items-center gap-2"><Lock size={18} className="text-[#D4AF37]" /> Salas Privadas</h2>
              <p className="text-[#9CA3AF] text-sm mt-1">Jogue só com amigos — crie uma sala com senha ou entre com um código.</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setPrivModal("create")} data-testid="create-private-btn" className="btn-gold px-4 py-2.5 rounded-full text-sm">Criar sala</button>
              <button onClick={() => setPrivModal("join")} data-testid="join-private-btn" className="px-4 py-2.5 rounded-full text-sm border border-white/15 text-white hover:bg-white/5 transition-colors">Entrar com código</button>
            </div>
          </div>
        </div>

        <div className="mt-8 dr-card p-6" data-testid="waiting-rooms">
          <h2 className="font-head text-xl font-bold mb-4">Salas Abertas</h2>
          {(data?.rooms || []).length === 0 ? (
            <p className="text-[#9CA3AF] text-sm">Nenhuma sala aguardando no momento. Entre numa aposta para abrir uma mesa.</p>
          ) : (
            <div className="space-y-2">
              {data.rooms.map((r, i) => (
                <div key={i} className="flex items-center justify-between bg-[#0F1319] rounded-lg px-4 py-3">
                  <div className="flex items-center gap-3">
                    <img src={r.avatar || "https://api.dicebear.com/7.x/initials/svg?seed=" + r.host} className="w-8 h-8 rounded-full" alt="" />
                    <div><div className="font-semibold text-sm">{r.host}</div><div className="text-[10px] text-[#9CA3AF]">Nível {r.level} • aguardando adversário</div></div>
                  </div>
                  <button onClick={() => enter(r.stake, r.currency)} className="btn-gold px-4 py-2 rounded-full text-sm">Entrar {r.currency === "virtual" ? `${r.stake} fichas` : `R$ ${r.stake}`}</button>
                </div>
              ))}
            </div>
          )}
        </div>

        {bal <= 0 && (
          <div className="mt-6 flex items-center gap-3 dr-card p-4 border-[#F59E0B]/40">
            <Wallet className="text-[#F59E0B]" />
            <p className="text-sm text-[#e5e7eb]">{isVirtual ? "Você está sem fichas." : "Você não tem créditos em reais."} <button onClick={() => navigate("/carteira")} className="underline text-[#F3E5AB]" data-testid="lobby-deposit-link">{isVirtual ? "Resgatar fichas grátis" : "Depositar via Pix/Cartão"}</button> para jogar.</p>
          </div>
        )}
      </main>

      {privModal === "create" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4" onClick={() => setPrivModal(null)}>
          <div className="dr-card max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="create-private-modal">
            <h3 className="font-head font-bold text-xl mb-4 flex items-center gap-2"><Lock size={18} className="text-[#D4AF37]" /> Criar sala privada</h3>
            <label className="block mb-3"><span className="text-xs text-[#9CA3AF]">Nome da sala</span><input value={pName} onChange={(e) => setPName(e.target.value)} placeholder="Mesa dos amigos" data-testid="private-name-input" className="w-full mt-1 bg-[#0F1319] border border-white/10 rounded-lg px-3 py-2.5 outline-none focus:border-[#D4AF37]" /></label>
            <label className="block mb-3"><span className="text-xs text-[#9CA3AF]">Senha (opcional)</span><input value={pPass} onChange={(e) => setPPass(e.target.value)} placeholder="senha" data-testid="private-password-input" className="w-full mt-1 bg-[#0F1319] border border-white/10 rounded-lg px-3 py-2.5 outline-none focus:border-[#D4AF37]" /></label>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <label className="block"><span className="text-xs text-[#9CA3AF]">Moeda</span>
                <select value={pCur} onChange={(e) => { setPCur(e.target.value); setPStake(e.target.value === "virtual" ? 100 : 5); }} data-testid="private-currency-select" className="w-full mt-1 bg-[#0F1319] border border-white/10 rounded-lg px-3 py-2.5 outline-none focus:border-[#D4AF37]">
                  <option value="real">Reais (R$)</option><option value="virtual">Fichas</option>
                </select>
              </label>
              <label className="block"><span className="text-xs text-[#9CA3AF]">Aposta</span>
                <select value={pStake} onChange={(e) => setPStake(Number(e.target.value))} data-testid="private-stake-select" className="w-full mt-1 bg-[#0F1319] border border-white/10 rounded-lg px-3 py-2.5 outline-none focus:border-[#D4AF37]">
                  {(pCur === "virtual" ? [100, 500, 1000, 5000] : [1, 2, 5, 10, 20]).map((s) => <option key={s} value={s}>{pCur === "virtual" ? `${s} fichas` : `R$ ${s}`}</option>)}
                </select>
              </label>
            </div>
            <button onClick={createPrivate} data-testid="private-create-confirm" className="btn-gold w-full py-3 rounded-full">Criar e aguardar amigo</button>
          </div>
        </div>
      )}

      {privModal === "join" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4" onClick={() => setPrivModal(null)}>
          <div className="dr-card max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="join-private-modal">
            <h3 className="font-head font-bold text-xl mb-4 flex items-center gap-2"><Lock size={18} className="text-[#D4AF37]" /> Entrar em sala privada</h3>
            <label className="block mb-3"><span className="text-xs text-[#9CA3AF]">Código da sala</span><input value={jCode} onChange={(e) => setJCode(e.target.value)} placeholder="ABC12" data-testid="join-code-input" className="w-full mt-1 bg-[#0F1319] border border-white/10 rounded-lg px-3 py-2.5 font-mono-num uppercase tracking-widest outline-none focus:border-[#D4AF37]" /></label>
            <label className="block mb-4"><span className="text-xs text-[#9CA3AF]">Senha (se houver)</span><input value={jPass} onChange={(e) => setJPass(e.target.value)} placeholder="senha" data-testid="join-password-input" className="w-full mt-1 bg-[#0F1319] border border-white/10 rounded-lg px-3 py-2.5 outline-none focus:border-[#D4AF37]" /></label>
            <button onClick={joinPrivate} data-testid="private-join-confirm" className="btn-gold w-full py-3 rounded-full">Entrar</button>
          </div>
        </div>
      )}

      {showAnn && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4" data-testid="announcement-modal">
          <div className="dr-card max-w-md w-full p-6 fade-up">
            <div className="flex items-center gap-2 mb-3"><Megaphone className="text-[#D4AF37]" /> <h3 className="font-head font-bold text-xl">Aviso</h3></div>
            <p className="text-[#d1d5db]">{branding?.announcement_text}</p>
            <button onClick={dismissAnn} data-testid="announcement-dismiss" className="btn-gold w-full mt-6 py-3 rounded-full">Entendi</button>
          </div>
        </div>
      )}
    </div>
  );
}
